/**
 * Gère la priorité des éléments flottants en bas d'écran (mobile).
 *
 * Hiérarchie :
 * 1. Sticky add to cart (fiche produit)
 * 2. Popup / teaser promo Klaviyo
 * 3. Bouton fidélité Smile
 *
 * Un seul élément fixe visible à la fois ; les secondaires sont masqués
 * quand un élément prioritaire est actif ou sur fiche produit.
 */
(function () {
  const MOBILE_MQ = window.matchMedia('(max-width: 768.98px)');

  const WIDGETS = {
    loyalty: ['#smile-ui-lite-container'],
    promo: ['.kl-teaser-WG2DFv', '[class*="kl-teaser-"]'],
  };

  const MODAL_SELECTORS = 'modal-dialog[open], pop-up[open], age-verification-pop-up[open]';
  const RESTORE_ATTR = 'data-prev-inline-display';

  /**
   * @param {string[]} selectors
   * @returns {Element[]}
   */
  function getTargets(selectors) {
    const seen = new Set();
    return selectors.flatMap((sel) => Array.from(document.querySelectorAll(sel))).filter((el) => {
      if (seen.has(el)) return false;
      seen.add(el);
      return true;
    });
  }

  /**
   * @param {Element} root
   * @returns {boolean}
   */
  function subtreeMatchesAnySelector(root) {
    const allSelectors = [...WIDGETS.loyalty, ...WIDGETS.promo];
    return allSelectors.some((sel) => {
      try {
        return root.matches(sel) || Boolean(root.querySelector(sel));
      } catch {
        return false;
      }
    });
  }

  /**
   * @returns {boolean}
   */
  function isMobile() {
    return MOBILE_MQ.matches;
  }

  /**
   * @returns {boolean}
   */
  function isProductPage() {
    return document.body.dataset.pageType === 'product';
  }

  /**
   * @returns {boolean}
   */
  function isStickyAtcActive() {
    const panel = document.querySelector('sticky-atc-panel');
    if (!panel) return false;
    return (
      !panel.classList.contains('sticky-atc-panel--out')
      && !document.body.classList.contains('scrolled-to-bottom')
    );
  }

  /**
   * @returns {boolean}
   */
  function isBlockingUiOpen() {
    const overlay = document.querySelector('.js-overlay');
    const overlayVisible = overlay && overlay.classList.contains('is-visible');
    const mobileMainOpen = document.querySelector('main-menu .main-menu__disclosure.is-open');
    const cartOpen = document.querySelector('#cart-drawer[open]');
    const quickBuyOpen = document.querySelector('quick-add-drawer[open]');
    return Boolean(overlayVisible || mobileMainOpen || cartOpen || quickBuyOpen);
  }

  /**
   * @returns {boolean}
   */
  function isAnyPopupOpen() {
    return Boolean(document.querySelector(MODAL_SELECTORS));
  }

  /**
   * @param {Element} el
   * @returns {boolean}
   */
  function isElementVisible(el) {
    if (!el || el.hasAttribute(RESTORE_ATTR)) return false;
    const style = window.getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
  }

  /**
   * @returns {boolean}
   */
  function isPromoVisible() {
    return getTargets(WIDGETS.promo).some(isElementVisible);
  }

  /**
   * @returns {boolean}
   */
  function shouldHideLoyalty() {
    if (isBlockingUiOpen()) return true;
    if (isAnyPopupOpen()) return true;
    if (isMobile() && isProductPage()) return true;
    if (isMobile() && isStickyAtcActive()) return true;
    if (isMobile() && isPromoVisible()) return true;
    return false;
  }

  /**
   * @returns {boolean}
   */
  function shouldHidePromo() {
    if (isBlockingUiOpen()) return true;
    if (isAnyPopupOpen()) return true;
    if (isMobile() && isProductPage()) return true;
    if (isMobile() && isStickyAtcActive()) return true;
    return false;
  }

  /**
   * @param {Element} el
   * @param {boolean} hide
   */
  function setVisibility(el, hide) {
    if (hide) {
      if (!el.hasAttribute(RESTORE_ATTR)) {
        el.setAttribute(RESTORE_ATTR, el.style.display);
      }
      el.style.display = 'none';
      return;
    }

    if (el.hasAttribute(RESTORE_ATTR)) {
      const prev = el.getAttribute(RESTORE_ATTR);
      if (prev === '' || prev == null) {
        el.style.removeProperty('display');
      } else {
        el.style.display = prev;
      }
      el.removeAttribute(RESTORE_ATTR);
    }
  }

  function applyVisibility() {
    const hideLoyalty = shouldHideLoyalty();
    const hidePromo = shouldHidePromo();

    getTargets(WIDGETS.loyalty).forEach((el) => setVisibility(el, hideLoyalty));
    getTargets(WIDGETS.promo).forEach((el) => setVisibility(el, hidePromo));

    document.body.classList.toggle('floating-ui-blocked', isBlockingUiOpen());
    document.body.classList.toggle('sticky-atc-active', isStickyAtcActive());
    document.body.classList.toggle('popup-open', isAnyPopupOpen());
  }

  function scheduleApply() {
    queueMicrotask(applyVisibility);
  }

  document.addEventListener('on:cart-drawer:before-open', scheduleApply);
  document.addEventListener('on:cart-drawer:after-open', scheduleApply);
  document.addEventListener('on:cart-drawer:after-close', scheduleApply);

  document.addEventListener('on:quickbuy:before-open', scheduleApply);
  document.addEventListener('on:quickbuy:after-open', scheduleApply);
  document.addEventListener('on:quickbuy:after-close', scheduleApply);

  document.addEventListener('on:sticky-atc:change', scheduleApply);

  const menuDisclosure = document.querySelector('main-menu .main-menu__disclosure');
  if (menuDisclosure) {
    const moMenu = new MutationObserver(scheduleApply);
    moMenu.observe(menuDisclosure, { attributes: true, attributeFilter: ['class'] });
  }

  const overlayEl = document.querySelector('.js-overlay');
  if (overlayEl) {
    const moOverlay = new MutationObserver(scheduleApply);
    moOverlay.observe(overlayEl, { attributes: true, attributeFilter: ['class'] });
  }

  const stickyPanel = document.querySelector('sticky-atc-panel');
  if (stickyPanel) {
    const moSticky = new MutationObserver(scheduleApply);
    moSticky.observe(stickyPanel, { attributes: true, attributeFilter: ['class'] });
  }

  const modalMo = new MutationObserver(scheduleApply);
  modalMo.observe(document.body, {
    attributes: true,
    attributeFilter: ['open'],
    subtree: true,
  });

  MOBILE_MQ.addEventListener('change', scheduleApply);
  window.addEventListener('scroll', scheduleApply, { passive: true });

  /** Widgets injectés après coup (apps / scripts). */
  const injectedFloatingMo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type !== 'childList') continue;
      for (const node of m.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        if (subtreeMatchesAnySelector(node)) {
          scheduleApply();
          return;
        }
      }
    }
  });
  injectedFloatingMo.observe(document.body, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleApply);
  } else {
    scheduleApply();
  }
})();
